import React from "react"
import { 
  Layers, Package, Building2, FileCheck2, MapPin, ArrowUpRight
} from "lucide-react"
import { cn } from "@/lib/utils"

export interface TargetItemViewProps {
  target: any
  onCompanyClick?: (company: any) => void
  onVariantClick?: (variant: any) => void
  onProductClick?: (product: any) => void
  onExternalSellerClick?: (seller: any) => void
  showSpecsGrid?: boolean
}

export function TargetItemView({ 
  target, 
  onCompanyClick,
  onVariantClick,
  onProductClick,
  onExternalSellerClick,
  showSpecsGrid = false
}: TargetItemViewProps) {
  if (!target) return null

  // 1. Early Return for Product Variant Target
  if (target.productVariant) {
    const pv = target.productVariant
    const variantName = pv.name

    const nameParts = variantName.trim().split(/\s+/)
    const logoInitials = nameParts.length > 1 
      ? (nameParts[0][0] + nameParts[1][0]).toUpperCase() 
      : nameParts[0].substring(0, 2).toUpperCase()

    return (
      <div 
        onClick={() => onVariantClick?.(pv)}
        className={cn(
          "group/item relative flex flex-col gap-3 rounded-xl border border-primary/15 bg-slate-50/40 p-4 text-left shadow-sm transition-all duration-300 overflow-hidden",
          onVariantClick ? "cursor-pointer hover:-translate-y-[2px] hover:shadow-md hover:border-primary hover:bg-primary/5" : ""
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-white shadow-sm text-slate-500 font-extrabold text-[15px] tracking-widest transition-all duration-300 group-hover/item:text-primary group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:scale-105">
            {logoInitials}
          </div>
          <div className="flex-1 min-w-0 pr-0 sm:pr-32">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className={cn("font-extrabold text-slate-800 text-[14px] tracking-tight transition-colors", onVariantClick && "group-hover/item:text-primary")}>
                {variantName}
              </h4>
            </div>
            
            {pv.description && (
              <p className="text-[12px] text-slate-800 font-medium leading-relaxed line-clamp-2 mt-0.5">
                {pv.description}
              </p>
            )}
          </div>
        </div>

        {/* Action Right */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 sm:opacity-100 flex items-center justify-end pointer-events-none">
          <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 shadow-sm transition-all duration-300 group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:text-primary group-hover/item:shadow-md">
            <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover/item:-translate-y-[1px] group-hover/item:translate-x-[1px]" />
          </div>
        </div>
      </div>
    )
  }

  // 2. Early Return for Product Target
  if (target.product) {
    const p = target.product

    const nameParts = (p.productName || "NA").trim().split(/\s+/)
    const logoInitials = nameParts.length > 1 
      ? (nameParts[0][0] + nameParts[1][0]).toUpperCase() 
      : nameParts[0].substring(0, 2).toUpperCase()

    return (
      <div 
        onClick={() => onProductClick?.(p)}
        className={cn(
          "group/item relative flex flex-col gap-3 rounded-xl border border-primary/15 bg-slate-50/40 p-4 text-left shadow-sm transition-all duration-300 overflow-hidden",
          onProductClick ? "cursor-pointer hover:-translate-y-[2px] hover:shadow-md hover:border-primary hover:bg-primary/5" : ""
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-white shadow-sm text-slate-500 font-extrabold text-[15px] tracking-widest transition-all duration-300 group-hover/item:text-primary group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:scale-105">
            {logoInitials}
          </div>
          <div className="flex-1 min-w-0 pr-0 sm:pr-32">
            <h4 className={cn("font-extrabold text-slate-800 text-[14px] tracking-tight transition-colors", onProductClick && "group-hover/item:text-primary")}>
              {p.productName}
            </h4>
            
            {p.description && (
              <p className="text-[12px] text-slate-500 font-medium leading-relaxed line-clamp-2">
                {p.description}
              </p>
            )}


          </div>
        </div>

        {/* Action Right */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 sm:opacity-100 flex items-center justify-end pointer-events-none">
          <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 shadow-sm transition-all duration-300 group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:text-primary group-hover/item:shadow-md">
            <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover/item:-translate-y-[1px] group-hover/item:translate-x-[1px]" />
          </div>
        </div>
      </div>
    )
  }

  // 3. Early Return for Company Target
  if (target.company) {
    const c = target.company
    const quantityText = target.quantity ? `Quantity: ${target.quantity}` : ""

    const nameParts = (c.name || "NA").trim().split(/\s+/)
    const logoInitials = nameParts.length > 1 
      ? (nameParts[0][0] + nameParts[1][0]).toUpperCase() 
      : nameParts[0].substring(0, 2).toUpperCase()

    const locationString = [c.city, c.country].filter(Boolean).join(", ")

    return (
      <div 
        onClick={() => onCompanyClick?.(c)}
        className={cn(
          "group/item relative flex flex-col gap-3 rounded-xl border border-primary/15 bg-slate-50/40 p-4 text-left shadow-sm transition-all duration-300 overflow-hidden",
          onCompanyClick ? "cursor-pointer hover:-translate-y-[2px] hover:shadow-md hover:border-primary hover:bg-primary/5" : ""
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-white shadow-sm text-slate-500 font-extrabold text-[15px] tracking-widest transition-all duration-300 group-hover/item:text-primary group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:scale-105">
            {logoInitials}
          </div>
          <div className="flex-1 min-w-0 pr-0 sm:pr-32">
            <h4 className={cn("font-extrabold text-slate-800 text-[14px] tracking-tight transition-colors", onCompanyClick && "group-hover/item:text-primary")}>
              {c.name}
            </h4>
            
            {c.description && (
              <p className="text-[12px] text-slate-500 font-medium leading-relaxed line-clamp-2">
                {c.description}
              </p>
            )}
          </div>
        </div>

        {/* Action Right */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 sm:opacity-100 flex items-center justify-end pointer-events-none">
          <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 shadow-sm transition-all duration-300 group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:text-primary group-hover/item:shadow-md">
            <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover/item:-translate-y-[1px] group-hover/item:translate-x-[1px]" />
          </div>
        </div>
      </div>
    )
  }

  // 4. Early Return for External Seller Target
  if (target.externalSeller) {
    const s = target.externalSeller
    const quantityText = target.quantity ? `Quantity: ${target.quantity}` : ""
    
    const nameParts = (s.companyName || "NA").trim().split(/\s+/)
    const logoInitials = nameParts.length > 1 
      ? (nameParts[0][0] + nameParts[1][0]).toUpperCase() 
      : nameParts[0].substring(0, 2).toUpperCase()

    const locationString = [s.locality, s.region, s.country].filter(Boolean).join(", ")

    return (
      <div 
        onClick={() => onExternalSellerClick?.(s)}
        className={cn(
          "group/item relative flex flex-col gap-3 rounded-xl border border-primary/15 bg-slate-50/40 p-4 text-left shadow-sm transition-all duration-300 overflow-hidden",
          onExternalSellerClick ? "cursor-pointer hover:-translate-y-[2px] hover:shadow-md hover:border-primary hover:bg-primary/5" : ""
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-white shadow-sm text-slate-500 font-extrabold text-[15px] tracking-widest transition-all duration-300 group-hover/item:text-primary group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:scale-105">
            {logoInitials}
          </div>
          <div className="flex-1 min-w-0 pr-0 sm:pr-32">
            <h4 className={cn("font-extrabold text-slate-800 text-[14px] tracking-tight transition-colors", onExternalSellerClick && "group-hover/item:text-primary")}>
              {s.companyName}
            </h4>

            {s.description && (
              <p className="text-[12px] text-slate-500 font-medium leading-relaxed line-clamp-2">
                {s.description}
              </p>
            )}
            
          </div>
        </div>

        {/* Action Right */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 sm:opacity-100 flex items-center justify-end pointer-events-none">
          <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 shadow-sm transition-all duration-300 group-hover/item:border-primary group-hover/item:bg-primary/5 group-hover/item:text-primary group-hover/item:shadow-md">
            <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover/item:-translate-y-[1px] group-hover/item:translate-x-[1px]" />
          </div>
        </div>
      </div>
    )
  }

  // 5. Generic Fallback
  const isProduct = !!target.product
  const isCompany = !!target.company
  const isVariant = !!target.productVariant

  let title = "Sourcing Inquiry"
  let desc = ""
  let tags: string[] = []
  let subtitle = ""

  if (isVariant) {
    const pv = target.productVariant
    const p = pv.product || {}
    const company = p.company || {}
    title = pv.name || p.productName || "Unnamed Variant"
    desc = pv.description || ""
    tags = pv.tags || []
    subtitle = `SKU: ${pv.sku || "N/A"}${company.name ? ` • Company: ${company.name}` : ""}`
  } else if (isProduct) {
    title = target.product.productName || "Unnamed Product"
    desc = target.product.description || ""
    tags = target.product.tags || []
    if (target.product.productCategory) {
      subtitle = `${target.product.productCategory}${target.product.productSubCategory ? ` • ${target.product.productSubCategory}` : ""}`
    }
  } else if (isCompany) {
    title = target.company.name || "Unnamed Company"
    desc = target.company.description || ""
    tags = target.company.tags || []
    subtitle = `${target.company.city || "Company"}${target.company.country ? ` • ${target.company.country}` : ""}`
  } else if (target.productId) {
    title = "Product Reference"
  } else if (target.companyId) {
    title = "Company Reference"
  }

  // return (
  //   <div className="group/item flex flex-col gap-3 rounded-xl border border-primary/15 hover:border-primary/30 bg-slate-50/40 hover:bg-primary/[0.02] p-3 text-left shadow-sm transition-all duration-200">
  //     <div className="flex items-start gap-4">
  //       <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-white shadow-sm text-slate-500 group-hover/item:text-primary group-hover/item:border-primary/20 transition-all duration-200">
  //         {isVariant ? (
  //           <Layers className="size-4" />
  //         ) : isProduct ? (
  //           <FileCheck2 className="size-4" />
  //         ) : isCompany ? (
  //           <Building2 className="size-4" />
  //         ) : (
  //           <Package className="size-4" />
  //         )}
  //       </div>
  //       <div className="space-y-1 flex-1 min-w-0">
  //         <div className="flex flex-wrap items-center gap-2">
  //           <h4 className="font-medium text-slate-800 text-[12px] tracking-tight">{title}</h4>
  //           {tags.length > 0 && (
  //             <div className="flex flex-wrap gap-1">
  //               {tags.map((t: string) => (
  //                 <span key={t} className="rounded px-1.5 py-0.5 text-[9px] font-bold bg-[#e6202f]/5 text-[#e6202f] border border-[#e6202f]/10 uppercase tracking-wide">
  //                   {t}
  //                 </span>
  //               ))}
  //             </div>
  //           )}
  //         </div>
          
  //         {subtitle && (
  //           <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
  //             {subtitle}
  //           </p>
  //         )}

  //         {desc && (
  //           <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-1">
  //             {desc}
  //           </p>
  //         )}
  //       </div>
  //     </div>
  //   </div>
  // )
}
